import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../util/firebase";
import { useAuth } from "../util/auth";

/**
 * Custom hook to fetch user generations from Firestore.
 * @returns {{
 *   generations: Array<object>;
 *   loading: boolean;
 *   error: string | null;
 *   refetchGenerations: () => Promise<void>;
 *   setGenerations: React.Dispatch<React.SetStateAction<Array<object>>>;
 * }}
 */
function useUserGenerations() {
  const auth = useAuth();
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const parseGenerationDoc = (docSnap) => {
    const data = docSnap.data();
    // Helper to safely convert Firestore Timestamps or date strings to Date objects
    const safeToDate = (field) => {
      if (!field) return null;
      if (field.toDate) return field.toDate(); // Firestore Timestamp
      if (typeof field === "string" || typeof field === "number")
        return new Date(field); // ISO string or timestamp number
      return null; // Or handle other types as needed
    };

    return {
      id: docSnap.id,
      ...data,
      start: safeToDate(data.start),
      end: safeToDate(data.end),
      createdAt: safeToDate(data.createdAt),
      timestamp: safeToDate(data.timestamp),
    };
  };

  const fetchGenerations = useCallback(async () => {
    if (!auth.user) {
      setGenerations([]); // Clear generations if user logs out
      return;
    }

    setLoading(true);
    setError(null);
    const userGenerationsRef = collection(
      db,
      "users",
      auth.user.uid,
      "generations"
    );
    const q = query(
      userGenerationsRef,
      orderBy("timestamp", "desc"),
      limit(20) // Consider making limit configurable or removing if pagination is added
    );

    try {
      const querySnapshot = await getDocs(q);
      const loadedGenerations = querySnapshot.docs.map(parseGenerationDoc);
      setGenerations(loadedGenerations);
    } catch (err) {
      console.error("Error loading generations from Firestore:", err);
      setError(
        "Failed to load your generated content history. Please try refreshing."
      );
    } finally {
      setLoading(false);
    }
  }, [auth.user]); // Dependency: re-run if auth.user changes

  useEffect(() => {
    fetchGenerations();
  }, [fetchGenerations]); // Fetch on mount and when fetchGenerations changes (due to auth.user change)

  return {
    generations,
    loading,
    error,
    refetchGenerations: fetchGenerations,
    setGenerations,
  };
}

export default useUserGenerations;
